// v4 (V4-H2, spec-v4 5.3/5.4): the cards the tree screens share: a collection tile (Home), a video poster from a
// VideoCard (Home's one-offs, a collection's videos) with its next step on one line, and the "New collection" sheet.
import { useState, type FormEvent } from "react";
import { collections, collectionsChanged, GROUP_OPTIONS, groupWord, needsYou, toastError, type CollectionCard, type SettingsGroup, type VideoCard } from "../collections";
import { director } from "../director";
import type { NextStepData } from "../nextstep";
import { Link, navigate } from "../router";
import { useStore } from "../store";
import { Sheet, statusOf, timeAgo } from "./bits";
import { FormatArt } from "./FormatArt";
import { FormatBadge } from "./FormatMark";
import { PosterPreview } from "./PosterPreview";
import { OlderBadge } from "./OlderLook";

const quiet = (s: NextStepData | null | undefined) => !s || !s.label || s.id === "watch" || s.id === "busy" || s.id === "none";

/** A collection on Home: its picture, name, kind and size, what needs you, and its next step. */
export function CollectionTile({ c, next }: { c: CollectionCard; next?: NextStepData | null }) {
  const need = needsYou(c.needs);
  const pic = c.thumbUrl ?? c.coverUrl;
  const step = quiet(next) ? null : next!;
  return (
    <Link to={`/k/${encodeURIComponent(c.id)}`} className="ktile" data-kind={c.kind}>
      <span className="ktile-frame">
        {pic ? <img src={pic} alt="" loading="lazy" /> : <FormatArt format={c.formats[0] ?? "cartoon"} className="ktile-art" />}
        {need > 0 && (
          <span className="ktile-need" aria-label={`${need} need you`}>
            <span className="ktile-need-dot" aria-hidden />
            {need}
          </span>
        )}
      </span>
      <span className="ktile-name">{c.name}</span>
      <span className="ktile-meta">
        {groupWord(c.kind, c.group)} · {c.videoCount} video{c.videoCount === 1 ? "" : "s"}
      </span>
      {step && <span className={`ktile-next${step.blocking && step.blocking.tone !== "info" ? " is-blocked" : ""}`}>{step.label}</span>}
    </Link>
  );
}

/** A video from the tree (no shots or brief here): poster or the format's plate, status, badge, title, next step. */
export function VideoPoster({ v, next, older = 0 }: { v: VideoCard; next?: NextStepData | null; older?: number }) {
  const live = useStore((s) => s.renders[v.id]);
  const st = statusOf(v, live);
  const step = quiet(next) ? null : next!;
  return (
    <Link data-action="video.open" to={`/p/${encodeURIComponent(v.id)}`} className="poster vposter">
      <span className="poster-frame">
        {v.thumbUrl ? (
          <PosterPreview poster={v.thumbUrl} preview={v.previewUrl} />
        ) : (
          <span className="poster-plate">
            <FormatArt format={v.format} />
            <span className="poster-plate-text">{v.title}</span>
          </span>
        )}
        <span className={`status status-${st.tone}`}>{st.label}</span>
        <FormatBadge format={v.format} overlay className="poster-badge" />
        {st.tone === "busy" && live && <span className="poster-progress" style={{ width: `${Math.round(live.progress * 100)}%` }} />}
      </span>
      <span className="poster-title">{v.title}</span>
      {/* older looks are a warning: never folded, always on the card */}
      <OlderBadge count={older} label={`${older} older shot${older === 1 ? "" : "s"}`} className="vposter-older" />
      {step ? <span className="vposter-next">{step.label}</span> : <span className="poster-meta">{timeAgo(v.updatedAt ?? undefined)}</span>}
    </Link>
  );
}

/** "New collection": what kind (one per settings group) and a name. Makes it in the channel and opens it. */
export function NewCollectionSheet({ channelId, onClose, start }: { channelId: string; onClose: () => void; start?: SettingsGroup }) {
  const [group, setGroup] = useState<SettingsGroup>(start ?? "story");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const opt = GROUP_OPTIONS.find((o) => o.group === group)!;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const c = await collections.create({ channelId, format: opt.format, name: name.trim() });
      collectionsChanged();
      director.context(`The user made a new ${opt.title.toLowerCase()} collection "${c.name}" (${c.id}) by tapping. It's open on their screen.`);
      onClose();
      navigate(`/k/${encodeURIComponent(c.id)}`);
    } catch (err) {
      toastError("Couldn't make the collection.", err);
      setBusy(false);
    }
  };
  return (
    <Sheet title="New collection" onClose={onClose} className="kc-sheet">
      <form className="sheet-body kc-form" onSubmit={submit}>
        <p className="sheet-note">Videos in a collection share what you set once: the cast, the look, the music, the narrator. You can move videos in later.</p>
        <div className="kc-kinds" role="radiogroup" aria-label="What kind of collection">
          {GROUP_OPTIONS.map((o) => (
            <button type="button" role="radio" aria-checked={o.group === group} key={o.group} className={`kc-kind${o.group === group ? " is-on" : ""}`} onClick={() => setGroup(o.group)}>
              <span className="kc-kind-art" aria-hidden>
                <FormatArt format={o.format} />
              </span>
              <span className="kc-kind-text">
                <span className="kc-kind-title">{o.title}</span>
                <span className="kc-kind-blurb">{o.blurb}</span>
              </span>
            </button>
          ))}
        </div>
        <label className="field">
          <span className="field-label">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={group === "story" ? "Milo & Friends" : group === "product" ? "Morning Roast" : "Name it"} maxLength={120} data-autofocus />
        </label>
        <button type="submit" className="btn btn-primary btn-block" disabled={busy || !name.trim()}>
          {busy ? "Making it" : `Make the ${opt.title.toLowerCase()}`}
        </button>
      </form>
    </Sheet>
  );
}
